import React, { useRef, useState } from 'react';
import { fileAccessApi } from '../../api/fileAccessApi';
import { ContractFile, ContractFileKind } from '../../api/contractsApi';
import { repairUploadFileName } from '../../utils/uploadFileName';

export interface PendingContractFile {
  file: File;
  fileKind: ContractFileKind;
  notes?: string;
}

const FILE_KIND_OPTIONS: Array<{ value: ContractFileKind; label: string }> = [
  { value: 'CONTRACT', label: 'Hợp đồng' },
  { value: 'APPENDIX', label: 'Phụ lục' },
  { value: 'AMENDMENT', label: 'Sửa đổi/Bổ sung' },
  { value: 'OTHER', label: 'Khác' },
];

const fileKindLabel = (kind: ContractFileKind) =>
  FILE_KIND_OPTIONS.find((o) => o.value === kind)?.label || kind;

interface ContractFileFieldProps {
  existingFiles: ContractFile[];
  pendingFiles: PendingContractFile[];
  onPendingFilesChange: (files: PendingContractFile[]) => void;
  onDeleteExisting?: (fileId: string) => void | Promise<void>;
  deletingFileIds?: string[];
}

export const ContractFileField: React.FC<ContractFileFieldProps> = ({
  existingFiles,
  pendingFiles,
  onPendingFilesChange,
  onDeleteExisting,
  deletingFileIds = [],
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileKind, setFileKind] = useState<ContractFileKind>('CONTRACT');
  const [notes, setNotes] = useState('');

  const handleAddPending = (file: File) => {
    onPendingFilesChange([...pendingFiles, { file, fileKind, notes: notes.trim() || undefined }]);
    setNotes('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDownload = async (fileUrl: string) => {
    const url = await fileAccessApi.getDownloadUrl(fileUrl);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const removePending = (index: number) => {
    onPendingFilesChange(pendingFiles.filter((_, i) => i !== index));
  };

  const hasFiles = existingFiles.length > 0 || pendingFiles.length > 0;

  return (
    <div className="md:col-span-2 space-y-3">
      <label className="block text-sm font-medium">File hợp đồng</label>

      <div className="flex flex-wrap items-end gap-3 p-3 border rounded bg-gray-50">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Loại file</label>
          <select
            className="border rounded px-2 py-1.5 text-sm"
            value={fileKind}
            onChange={(e) => setFileKind(e.target.value as ContractFileKind)}
          >
            {FILE_KIND_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs text-gray-600 mb-1">Ghi chú (tùy chọn)</label>
          <input
            className="w-full border rounded px-2 py-1.5 text-sm"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="VD: Phụ lục 02"
          />
        </div>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleAddPending(file);
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="px-3 py-2 rounded border border-gray-300 text-sm hover:bg-white"
        >
          Thêm file
        </button>
      </div>

      {!hasFiles ? (
        <p className="text-sm text-gray-500">Chưa có file hợp đồng</p>
      ) : (
        <div className="border rounded divide-y">
          {existingFiles.map((file) => (
            <div key={file.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
              <span className="inline-flex px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs">
                {fileKindLabel(file.fileKind)}
              </span>
              <span className="flex-1 truncate">{repairUploadFileName(file.fileName)}</span>
              {file.notes ? <span className="text-gray-500 text-xs">{file.notes}</span> : null}
              <button
                type="button"
                onClick={() => handleDownload(file.fileUrl)}
                className="text-cyan-600 hover:text-cyan-700"
              >
                Tải xuống
              </button>
              {onDeleteExisting ? (
                <button
                  type="button"
                  disabled={deletingFileIds.includes(file.id)}
                  onClick={() => onDeleteExisting(file.id)}
                  className="text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  {deletingFileIds.includes(file.id) ? 'Đang xóa...' : 'Xóa'}
                </button>
              ) : null}
            </div>
          ))}
          {pendingFiles.map((pending, index) => (
            <div key={`pending-${index}`} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm bg-amber-50">
              <span className="inline-flex px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">
                {fileKindLabel(pending.fileKind)} (chờ lưu)
              </span>
              <span className="flex-1 truncate">{pending.file.name}</span>
              {pending.notes ? <span className="text-gray-500 text-xs">{pending.notes}</span> : null}
              <button
                type="button"
                onClick={() => removePending(index)}
                className="text-red-600 hover:text-red-700"
              >
                Bỏ
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractFileField;
